import React from 'react';
import wrap from 'wraptile';
import {curry} from 'ramda';
import {
    ApTimelineMarker,
    ApTimelineRow,
    ApTimelineMetadata,
} from '@accurate-player/accurate-player-components-external';
import {Typography} from '@material-ui/core';

import './AdBreaks.scss';
import classNames from 'classnames';
import {TimelineMarker} from '../TimelineTypes';
import Diamond from './Diamond/Diamond';

ApTimelineMarker.register();
ApTimelineRow.register();
ApTimelineMetadata.register();

export interface AdBreaksProps {
    setMarkerRowContainerRef: () => void;
    onRowClicked: () => void;
    rowLabel: string;
    rowId: string;
    markers: TimelineMarker[];
    selectedRowId: string;
    selectedMarkerId: string;
    onDiamondClicked: (marker: TimelineMarker) => void;
}

export const onDiamondClick = wrap((marker: TimelineMarker, onDiamondClicked: (marker: TimelineMarker) => void) => {
    onDiamondClicked(marker);
});

export const getMarker = curry((selectedMarkerId: string, onDiamondClicked: (marker: TimelineMarker) => void, marker: TimelineMarker) => {
    const {
        identifier,
        start,
        end,
    } = marker;

    const selected = selectedMarkerId === identifier;
    const handleDiamondClicked = onDiamondClick(marker, onDiamondClicked);

    return (
        <ap-timeline-marker
            key={identifier}
            identifier={identifier}
            start={start}
            end={end}
            class="ad-breaks-marker"
        >
            <Diamond
                marker={marker}
                selected={selected}
                onDiamondClicked={handleDiamondClicked}
            />
        </ap-timeline-marker>
    );
});

export const getMarkers = (markers: TimelineMarker[], selectedMarkerId: string, onDiamondClicked: (marker: TimelineMarker) => void) => {
    if (!markers)
        return null;

    const handleGetMarker = getMarker(selectedMarkerId, onDiamondClicked);

    return markers.map(handleGetMarker);
};

export default function AdBreaks(props: AdBreaksProps) {
    const {
        markers,
        selectedMarkerId,
        selectedRowId,
        rowId,
        setMarkerRowContainerRef,
        onRowClicked,
        onDiamondClicked,
    } = props;
    const domMarkers = getMarkers(markers, selectedMarkerId, onDiamondClicked);
    const selected = selectedRowId === rowId;
    const adBreaksLength = markers?.length;
    const rowLabels = adBreaksLength > 1 ? `Ad breaks (${adBreaksLength})` : `Ad break (${adBreaksLength})`;

    return (
        <ap-timeline-row
            class={classNames('marker-row ad-breaks', {
                selected,
            })}
            onClick={onRowClicked}
            id={rowId}
        >
            <span
                slot="header"
                className="row-header"
            >
                <Typography
                    variant="body2"
                    color="textSecondary"
                >
                    {rowLabels}
                </Typography>
            </span>
            <ap-timeline-metadata
                ref={setMarkerRowContainerRef}
                padding={7}
                renderer-type="dom"
                class="ad-breaks-metadata-row"
            >
                {domMarkers}
            </ap-timeline-metadata>
        </ap-timeline-row>
    );
}
